import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FaqSchemas, type SaveFaqs } from "@workspace/contracts/faqs";
import type { SupportedLanguages } from "@workspace/contracts/language";
import { Button } from "@workspace/ui/components/button";
import { Link2Icon, PlusIcon, TrashIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { useAppForm } from "@/components/form-inputs";
import { LanguageSelector } from "@/components/language-selector";
import { useDashboardRole } from "@/hooks/use-dashboard-role";
import { Main } from "@/layout/main";
import { getError } from "@/utils/error";

export const Route = createFileRoute("/_authenticated/faqs")({
  loader: async ({ context: { orpc, queryClient } }) => {
    const faqs = await queryClient.ensureQueryData(orpc.admin.faqs.list.queryOptions({ input: {} }));
    return { faqs };
  },
  component: RouteComponent,
});

function emptyFaq(sortOrder: number) {
  return {
    sortOrder,
    published: true,
    translations: {
      en: { question: "", answer: "", links: [] },
      ar: { question: "", answer: "", links: [] },
      tr: { question: "", answer: "", links: [] },
      fa: { question: "", answer: "", links: [] },
    },
  };
}

function RouteComponent() {
  const role = useDashboardRole();
  const { faqs } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const [lang, setLang] = useState<SupportedLanguages>("en");
  const { mutateAsync } = useMutation(orpc.admin.faqs.save.mutationOptions());

  const form = useAppForm({
    defaultValues: { items: faqs } as SaveFaqs,
    validators: { onSubmit: FaqSchemas.save },
    onSubmit: async ({ value }) => {
      try {
        await mutateAsync(value);
        await queryClient.invalidateQueries({ queryKey: orpc.admin.faqs.list.queryKey({ input: {} }) });
        toast.success("FAQ content was saved successfully.");
      } catch (error) {
        toast.error(getError(error, "Failed to save FAQ content"));
      }
    },
  });

  return (
    <Main>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">FAQ</h1>
          <p className="text-muted-foreground text-sm">
            Manage the questions and answers shown on the public FAQ page.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => form.pushFieldValue("items", emptyFaq(form.getFieldValue("items").length * 10))}
        >
          <PlusIcon />
          Add FAQ
        </Button>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit();
        }}
        className="space-y-6"
      >
        <LanguageSelector lang={lang} setLang={setLang} />

        <form.AppField name="items" mode="array">
          {(itemsField) => (
            <div className="space-y-5">
              {itemsField.state.value.map((item, index) => (
                <section key={item.id ?? index} className="space-y-4 rounded-xl border bg-card p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="font-medium">FAQ {index + 1}</h2>
                    <div className="flex items-center gap-3">
                      <form.AppField name={`items[${index}].published`}>
                        {(field) => <field.CheckboxField label="Published" />}
                      </form.AppField>
                      {role === "admin" && (
                        <Button
                          type="button"
                          size="icon"
                          variant="destructive"
                          onClick={() => itemsField.removeValue(index)}
                        >
                          <TrashIcon />
                          <span className="sr-only">Delete FAQ {index + 1}</span>
                        </Button>
                      )}
                    </div>
                  </div>

                  <form.AppField name={`items[${index}].sortOrder`}>
                    {(field) => <field.NumberField label="Display order" />}
                  </form.AppField>

                  <form.AppField name={`items[${index}].translations.${lang}.question`}>
                    {(field) => <field.TextField label="Question" />}
                  </form.AppField>

                  <form.AppField name={`items[${index}].translations.${lang}.answer`}>
                    {(field) => <field.TextareaField label="Answer" />}
                  </form.AppField>

                  <form.AppField name={`items[${index}].translations.${lang}.links`} mode="array">
                    {(linksField) => (
                      <div className="space-y-3 rounded-lg bg-muted/40 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-sm">Related internal links</p>
                            <p className="text-muted-foreground text-xs">
                              Optional links shown beneath the answer. Use site paths such as /projects or /blog.
                            </p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={linksField.state.value.length >= 3}
                            onClick={() => linksField.pushValue({ label: "", href: "" })}
                          >
                            <Link2Icon />
                            Add link
                          </Button>
                        </div>

                        {linksField.state.value.map((link, linkIndex) => (
                          <div
                            key={`${link.href}-${linkIndex}`}
                            className="grid gap-3 rounded-md border bg-card p-3 md:grid-cols-[1fr_1fr_auto] md:items-end"
                          >
                            <form.AppField name={`items[${index}].translations.${lang}.links[${linkIndex}].label`}>
                              {(field) => <field.TextField label="Link label" />}
                            </form.AppField>
                            <form.AppField name={`items[${index}].translations.${lang}.links[${linkIndex}].href`}>
                              {(field) => <field.TextField label="Internal URL" placeholder="/projects" />}
                            </form.AppField>
                            <Button
                              type="button"
                              size="icon"
                              variant="destructive"
                              onClick={() => linksField.removeValue(linkIndex)}
                            >
                              <TrashIcon />
                              <span className="sr-only">Delete related link {linkIndex + 1}</span>
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </form.AppField>
                </section>
              ))}

              {itemsField.state.value.length === 0 && (
                <p className="rounded-xl border border-dashed p-8 text-center text-muted-foreground">
                  No FAQ entries yet. Add one to get started.
                </p>
              )}
            </div>
          )}
        </form.AppField>

        <div className="flex justify-end">
          <form.AppForm>
            <form.SubmitButton>Save FAQ</form.SubmitButton>
          </form.AppForm>
        </div>
      </form>
    </Main>
  );
}
